import { Gift } from 'lucide-react';
import { Toggle } from '../../../ui/Toggle';
import { ProductLinkInput } from '../../../ui/ProductLinkInput';
import { ProductManualInputs } from '../../../ui/ProductManualInputs';
import SearchPreviousProducts from '../../../../onboarding/components/SearchPreviousProducts';
import { dictionary } from '../../../../../utils/dictionary';
import { useAuth } from '../../../../../context/AuthContext';
import { useTutorialContext } from '../../../../../context/TutorialContext';
import { useOnboardingStore } from '../../../../../store/onboardingStore';
import { useBumpOffer } from '../hooks/useBumpOffer';
import { MethodSelector } from './MethodSelector';

interface OrderBumpCardProps {
  isScraping?: boolean;
}

export function OrderBumpCard({ isScraping = false }: OrderBumpCardProps) {
  const { language } = useAuth();
  const { setCurrentStepIndex, resumeTutorial } = useTutorialContext();
  const { previousProducts, isPreviousProductsLoading } = useOnboardingStore();
  const {
    bumpOffer,
    method,
    setMethod,
    handleToggle,
    handleChange,
    handlePreviousSelect,
    imageUpload,
  } = useBumpOffer();

  const handleOrderBump = () => {
    setCurrentStepIndex(2);
    resumeTutorial();
  };

  return (
    <div
      id="orderBump-target"
      className={`${bumpOffer.enabled ? 'bg-[#F6FFFA] border-[#E3FBEB] solid border-2' : 'bg-[#E3FBEB]'} from-green-50 to-emerald-50 rounded-[25px] px-6 py-4`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center bg-white">
            <Gift className="w-7 h-7 text-green-800" />
          </div>
          <div>
            <h3 className="text-md font-bold text-gray-900">{dictionary['add-order-bump'][language]}</h3>
            <p
              onClick={handleOrderBump}
              className="text-xs text-[#62748E] hover:text-blue-500 hover:underline transition-colors cursor-pointer"
            >
              {dictionary['what-is-an-order-bump'][language]}
            </p>
          </div>
        </div>
        <Toggle enabled={bumpOffer.enabled} onChange={handleToggle} />
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ${
          bumpOffer.enabled ? 'opacity-100 max-h-[500px]' : 'opacity-0 max-h-0'
        }`}
      >
        <MethodSelector currentMethod={method} onSelect={setMethod} disabled={isScraping} />

        {method === 'link' && (
          <div className="relative mt-3">
            <ProductLinkInput
              value={bumpOffer.productLink || ''}
              placeholder={dictionary['paste-product-url-placeholder'][language]}
              onChange={(value) => handleChange('productLink', value)}
            />
          </div>
        )}

        {method === 'manual' && (
          <div className="mt-3 space-y-3">
            <ProductManualInputs
              manualData={{
                images: bumpOffer.url_images ? [bumpOffer.url_images] : [],
                name: bumpOffer.productName || '',
                price: bumpOffer.price || '',
                description: bumpOffer.description || '',
              }}
              handleImageUpload={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const synthetic = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                  imageUpload.handleImageUpload(synthetic);
                }
              }}
              handleRemoveImage={() => imageUpload.removeImage(0)}
              handleManualDataChange={(field, value) => {
                const map: Record<string, string> = { name: 'productName', price: 'price', description: 'description' };
                handleChange(map[field] ?? field, value);
              }}
              isUploadingImages={imageUpload.isLoading}
            />
          </div>
        )}

        {method === 'previous' && (
          <div className="mt-3 space-y-3">
            <SearchPreviousProducts
              previousProducts={previousProducts}
              handlePreviousProductSelect={(e) => {
                const product = previousProducts.find((p) => p.id === e.target.value);
                if (product) handlePreviousSelect(product);
              }}
              isPreviousProductsLoading={isPreviousProductsLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
